<template>
  <v-dialog v-model="dialog" max-width="1400px" class="fontNotoSans400" persistent>
    <div class="wageBeforeAfterModalCard">
      <div class="modal-header">
        <h2>비과세 적용 전후 비교표</h2>
        <v-icon class="close-btn" color="#333" @click="closeDialog">mdi-close</v-icon>
      </div>

      <div class="info-section">
        <div class="info-row">
          <div class="info-item">
            <span class="info-label">조회 연월</span>
            <span class="info-value highlight">{{ currentYearMonth }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">서비스 시작연월</span>
            <template v-if="!isEditingServiceDate">
              <span class="info-value">{{ serviceStartDate || '미설정' }}</span>
              <v-btn variant="text" size="x-small" color="grey" @click="isEditingServiceDate = true" class="edit-btn">
                <v-icon size="small">mdi-pencil</v-icon>
              </v-btn>
            </template>
            <template v-else>
              <input type="month" v-model="editServiceDate" class="date-input" />
              <v-btn variant="text" size="x-small" color="primary" @click="saveServiceDate" :loading="isSavingDate">
                <v-icon size="small">mdi-check</v-icon>
              </v-btn>
              <v-btn variant="text" size="x-small" color="grey" @click="cancelEditServiceDate">
                <v-icon size="small">mdi-close</v-icon>
              </v-btn>
            </template>
          </div>
        </div>
      </div>

      <div class="action-section">
        <v-btn class="btnColorDark" @click="generatePdf" :disabled="comparisonData.length === 0">PDF 생성</v-btn>
      </div>

      <WageBeforeAfterHelp />

      <div class="modal-content" v-if="comparisonData.length > 0">
        <v-tabs v-model="activeTab" color="primary">
          <v-tab value="detail">상세 비교</v-tab>
          <v-tab value="summary">요약</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <v-window-item value="detail">
            <div class="unit-label">단위: 원</div>
            <div class="comparison-table-wrapper">
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th rowspan="2" class="sticky-col col-no">No</th>
                    <th rowspan="2" class="sticky-col-name">사원명</th>
                    <th rowspan="2">직종</th>
                    <th rowspan="2">총급여</th>
                    <th colspan="4" class="before-header">개선전</th>
                    <th colspan="4" class="after-header">개선후</th>
                    <th colspan="4" class="diff-header">차액</th>
                    <th rowspan="2" class="saving-header">절감액</th>
                  </tr>
                  <tr>
                    <th class="before-header">비과세</th>
                    <th class="before-header">기관부담</th>
                    <th class="before-header">퇴직금</th>
                    <th class="before-header">지급급여</th>
                    <th class="after-header">비과세</th>
                    <th class="after-header">기관부담</th>
                    <th class="after-header">퇴직금</th>
                    <th class="after-header">지급급여</th>
                    <th class="diff-sub-header">비과세</th>
                    <th class="diff-sub-header">기관부담</th>
                    <th class="diff-sub-header">퇴직금</th>
                    <th class="diff-sub-header">지급급여</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in comparisonData" :key="index">
                    <td class="sticky-col col-no text-center">{{ index + 1 }}</td>
                    <td class="sticky-col-name">{{ item.empName }}</td>
                    <td class="text-center">{{ item.empJob }}</td>
                    <td class="text-right tooltip-cell" :title="item.tooltip.total">{{ item.total }}</td>
                    <td class="before-cell text-right tooltip-cell" :title="item.tooltip.beforeNonTax">{{ item.before.nonTax }}</td>
                    <td class="before-cell text-right tooltip-cell" :title="item.tooltip.beforeCompanyIns">{{ item.before.companyIns }}</td>
                    <td class="before-cell text-right tooltip-cell" :title="item.tooltip.beforeRetirement">{{ item.before.retirement }}</td>
                    <td class="before-cell text-right tooltip-cell" :title="item.tooltip.beforeNetPay">{{ item.before.netPay }}</td>
                    <td class="after-cell text-right tooltip-cell" :title="item.tooltip.afterNonTax">{{ item.after.nonTax }}</td>
                    <td class="after-cell text-right tooltip-cell" :title="item.tooltip.afterCompanyIns">{{ item.after.companyIns }}</td>
                    <td class="after-cell text-right tooltip-cell" :title="item.tooltip.afterRetirement">{{ item.after.retirement }}</td>
                    <td class="after-cell text-right tooltip-cell" :title="item.tooltip.afterNetPay">{{ item.after.netPay }}</td>
                    <td class="diff-cell text-right tooltip-cell" :class="getDiffClass(item.diff.nonTax)" :title="item.tooltip.diffNonTax">{{ item.diff.nonTax }}</td>
                    <td class="diff-cell text-right tooltip-cell" :class="getDiffClass(item.diff.companyIns, true)" :title="item.tooltip.diffCompanyIns">{{ item.diff.companyIns }}</td>
                    <td class="diff-cell text-right tooltip-cell" :class="getDiffClass(item.diff.retirement, true)" :title="item.tooltip.diffRetirement">{{ item.diff.retirement }}</td>
                    <td class="diff-cell text-right tooltip-cell" :class="getDiffClass(item.diff.netPay)" :title="item.tooltip.diffNetPay">{{ item.diff.netPay }}</td>
                    <td class="saving-cell text-right font-bold tooltip-cell" :class="getDiffClass(item.saving, true)" :title="item.tooltip.saving">{{ item.saving }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td class="sticky-col col-no font-bold" colspan="3">합계</td>
                    <td class="text-right font-bold">{{ totals.total }}</td>
                    <td class="before-cell text-right font-bold">{{ totals.before.nonTax }}</td>
                    <td class="before-cell text-right font-bold">{{ totals.before.companyIns }}</td>
                    <td class="before-cell text-right font-bold">{{ totals.before.retirement }}</td>
                    <td class="before-cell text-right font-bold">{{ totals.before.netPay }}</td>
                    <td class="after-cell text-right font-bold">{{ totals.after.nonTax }}</td>
                    <td class="after-cell text-right font-bold">{{ totals.after.companyIns }}</td>
                    <td class="after-cell text-right font-bold">{{ totals.after.retirement }}</td>
                    <td class="after-cell text-right font-bold">{{ totals.after.netPay }}</td>
                    <td class="diff-cell text-right font-bold tooltip-cell" :class="getDiffClass(totals.diff.nonTax)" :title="totalsTooltip.nonTax">{{ totals.diff.nonTax }}</td>
                    <td class="diff-cell text-right font-bold tooltip-cell" :class="getDiffClass(totals.diff.companyIns, true)" :title="totalsTooltip.companyIns">{{ totals.diff.companyIns }}</td>
                    <td class="diff-cell text-right font-bold tooltip-cell" :class="getDiffClass(totals.diff.retirement, true)" :title="totalsTooltip.retirement">{{ totals.diff.retirement }}</td>
                    <td class="diff-cell text-right font-bold tooltip-cell" :class="getDiffClass(totals.diff.netPay)" :title="totalsTooltip.netPay">{{ totals.diff.netPay }}</td>
                    <td class="saving-cell text-right font-bold tooltip-cell" :class="getDiffClass(totals.saving, true)" :title="totalsTooltip.saving">{{ totals.saving }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </v-window-item>

          <v-window-item value="summary">
            <div class="summary-section">
              <div class="summary-cards">
                <div class="summary-card before-card">
                  <h3>개선전 (생산직비과세 미적용)</h3>
                  <div class="summary-row">
                    <span class="label">총급여</span>
                    <span class="value">{{ totals.total }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">비과세</span>
                    <span class="value">{{ totals.before.nonTax }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">기관부담 4대보험</span>
                    <span class="value">{{ totals.before.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">퇴직금</span>
                    <span class="value">{{ totals.before.retirement }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row total">
                    <span class="label">지급급여 (실수령액)</span>
                    <span class="value">{{ totals.before.netPay }}</span>
                  </div>
                </div>

                <div class="summary-card after-card">
                  <h3>개선후 (생산직비과세 적용)</h3>
                  <div class="summary-row">
                    <span class="label">총급여</span>
                    <span class="value">{{ totals.total }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">비과세</span>
                    <span class="value">{{ totals.after.nonTax }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">기관부담 4대보험</span>
                    <span class="value">{{ totals.after.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">퇴직금</span>
                    <span class="value">{{ totals.after.retirement }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row total">
                    <span class="label">지급급여 (실수령액)</span>
                    <span class="value">{{ totals.after.netPay }}</span>
                  </div>
                </div>

                <div class="summary-card diff-card">
                  <h3>기관 비용 절감 효과</h3>
                  <div class="summary-row highlight-row">
                    <span class="label">대상 인원</span>
                    <span class="value">{{ comparisonData.length }}명</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">기관부담 4대보험 차액</span>
                    <span class="value" :class="getDiffClass(totals.diff.companyIns, true)">{{ totals.diff.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">퇴직금 차액</span>
                    <span class="value" :class="getDiffClass(totals.diff.retirement, true)">{{ totals.diff.retirement }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row total">
                    <span class="label">월 절감액</span>
                    <span class="value large" :class="getDiffClass(totals.saving, true)">{{ totals.saving }}</span>
                  </div>
                  <div class="summary-row info">
                    <span class="label">연간 절감 예상액</span>
                    <span class="value" :class="getDiffClass(totals.saving, true)">{{ totals.yearSaving }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
        </v-window>
      </div>

      <div class="modal-content empty-state" v-else-if="!loadingBasic">
        <p>데이터가 없습니다.</p>
      </div>

      <div class="modal-content empty-state" v-else>
        <p>데이터를 불러오는 중...</p>
      </div>

      <div class="modal-footer">
        <v-btn class="cancelRegistBtn" @click="closeDialog">닫기</v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, reactive } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import WageBeforeAfterHelp from "./WageBeforeAfterHelp.vue";

interface LedgerItem {
  empName: string;
  empJob: string;
  empNum: string;
  sisulName: string;
  ym: string;
  total: string;
  taxable: string;
  dang: string;
  lunch: string;
  car: string;
  childcare: string;
  l_yeon: string;
  l_gun: string;
  l_jang: string;
  l_go: string;
  l_so: string;
  l_ji: string;
  l_jiwon: string;
  l_sik: string;
  l_etc: string;
  l_yearEnd: string;
  l_total: string;
  c_yeon: string;
  c_gun: string;
  c_jang: string;
  c_go: string;
  c_sil: string;
  c_san: string;
  c_out: string;
  empOutYm?: string;
}

interface TaxRate {
  tr_yeon: number;
  tr_gun: number;
  tr_jang: number;
  tr_go: number;
  tr_sil: number;
  tr_san: number;
}

interface BasicItem {
  empName: string;
  empJob: string;
  empNum: string;
  eaBasicPay: string;
  eaOverPay: string;
  eaNightPay: string;
  eaHolidayPay: string;
  eaDayOffPay: string;
  eaLongtermPay: string;
  eaSunimPay: string;
  eaBonus: string;
  eaEduPay: string;
  eaBojonPay: string;
  eaEtcPay: string;
  eaDangPay: string;
  eaLunchPay: string;
  eaCarPay: string;
  eaChildcarePay: string;
  eaMealDeduct: string;
  eaEtcDeduct: string;
  eaYearEnd: string;
}

interface ComparisonItem {
  empName: string;
  empJob: string;
  total: string;
  before: {
    nonTax: string;
    companyIns: string;
    retirement: string;
    netPay: string;
  };
  after: {
    nonTax: string;
    companyIns: string;
    retirement: string;
    netPay: string;
  };
  diff: {
    nonTax: string;
    companyIns: string;
    retirement: string;
    netPay: string;
  };
  saving: string;
  tooltip: {
    total: string;
    beforeNonTax: string;
    beforeCompanyIns: string;
    beforeRetirement: string;
    beforeNetPay: string;
    afterNonTax: string;
    afterCompanyIns: string;
    afterRetirement: string;
    afterNetPay: string;
    diffNonTax: string;
    diffCompanyIns: string;
    diffRetirement: string;
    diffNetPay: string;
    saving: string;
  };
}

const props = defineProps<{
  wageList: LedgerItem[];
  tax: TaxRate;
}>();

const store = useStore();
const dialog = ref(false);
const activeTab = ref("detail");
const loadingBasic = ref(false);
const basicList = ref<BasicItem[]>([]);
const comparisonData = ref<ComparisonItem[]>([]);

// 서비스 시작연월 관련
const serviceStartDate = ref<string>("");
const rawServiceStartYm = ref<string>(""); // YYYY-MM 형식
const isEditingServiceDate = ref(false);
const editServiceDate = ref("");
const isSavingDate = ref(false);
const currentYearMonth = ref("");
const rawCurrentYm = ref<string>(""); // YYYY-MM 형식

const totals = reactive({
  total: "0",
  before: {
    nonTax: "0", companyIns: "0", retirement: "0", netPay: "0"
  },
  after: {
    nonTax: "0", companyIns: "0", retirement: "0", netPay: "0"
  },
  diff: {
    nonTax: "0", companyIns: "0", retirement: "0", netPay: "0"
  },
  saving: "0",
  yearSaving: "0"
});

const totalsTooltip = reactive({
  nonTax: "",
  companyIns: "",
  retirement: "",
  netPay: "",
  saving: ""
});

const openDialog = async () => {
  dialog.value = true;
  activeTab.value = "detail";
  comparisonData.value = [];
  rawCurrentYm.value = store.state.AdminYYMM;
  currentYearMonth.value = formatYearMonth(store.state.AdminYYMM);
  await fetchServiceStartDate();
  await fetchBasicData();
};

defineExpose({ openDialog });

const closeDialog = () => {
  dialog.value = false;
};

const nc = (value: string | number | null | undefined): number => {
  if (value == null) return 0;
  return parseFloat(value.toString().replace(/,/g, "")) || 0;
};

const ac = (value: number): string => {
  if (isNaN(value)) return "0";
  return Math.round(value).toLocaleString();
};

const getDiffClass = (value: string, isReverse = false): string => {
  const num = nc(value);
  if (isReverse) {
    if (num < 0) return "highlight-green";
    if (num > 0) return "highlight-red";
  } else {
    if (num > 0) return "highlight-green";
    if (num < 0) return "highlight-red";
  }
  return "";
};

const formatYearMonth = (ym: string): string => {
  if (!ym) return "";
  const parts = ym.split("-");
  if (parts.length === 2) {
    return `${parts[0]}년 ${parseInt(parts[1], 10)}월`;
  }
  return ym;
};

const fetchServiceStartDate = async () => {
  try {
    const sisulId = store.state.AdminSisulId;
    const response = await axios.get(`/api/readSsId/${sisulId}`);
    if (response.data?.result) {
      const wageIn = response.data.result.sisul_wage_in;
      serviceStartDate.value = wageIn ? formatYearMonth(wageIn) : "";
      rawServiceStartYm.value = wageIn || "";
      editServiceDate.value = wageIn || "";
    }
  } catch (error) {
    console.error("서비스 시작연월 조회 실패:", error);
  }
};

const saveServiceDate = async () => {
  if (!editServiceDate.value) return;
  isSavingDate.value = true;
  try {
    const sisulId = store.state.AdminSisulId;
    await axios.post("/api/updateSsWageIn", {
      sisul_id: sisulId,
      sisul_wage_in: editServiceDate.value
    });
    serviceStartDate.value = formatYearMonth(editServiceDate.value);
    rawServiceStartYm.value = editServiceDate.value;
    isEditingServiceDate.value = false;
    store.commit("showSnackbar", { text: "서비스 시작연월이 저장되었습니다.", color: "success" });
    calculateComparison();
  } catch (error) {
    console.error("서비스 시작연월 저장 실패:", error);
    store.commit("showSnackbar", { text: "저장 중 오류가 발생했습니다.", color: "error" });
  } finally {
    isSavingDate.value = false;
  }
};

const cancelEditServiceDate = () => {
  isEditingServiceDate.value = false;
  const ym = store.state.AdminYYMM;
  editServiceDate.value = serviceStartDate.value ? ym : "";
};

const fetchBasicData = async () => {
  loadingBasic.value = true;
  try {
    const code = store.state.AdminSisulCode;
    const date = store.state.AdminYYMM;
    const response = await axios.get(`/api/getEmpWageBasic/${code}/${date}`);
    if (response.data?.result) {
      basicList.value = response.data.result;
      calculateComparison();
    }
  } catch (error) {
    console.error("임금기초 데이터 로드 실패:", error);
  } finally {
    loadingBasic.value = false;
  }
};

const calculateComparison = () => {
  if (!props.wageList || props.wageList.length === 0) return;
  if (!basicList.value || basicList.value.length === 0) return;

  const taxRate = props.tax;

  const sum = { total: 0 };
  const sumBefore = { nonTax: 0, companyIns: 0, retirement: 0, netPay: 0 };
  const sumAfter = { nonTax: 0, companyIns: 0, retirement: 0, netPay: 0 };

  // 서비스 시작연월 이전에 퇴사한 직원 제외
  const filteredWageList = rawServiceStartYm.value
    ? props.wageList.filter((ledger) => !ledger.empOutYm || ledger.empOutYm >= rawServiceStartYm.value)
    : props.wageList;

  comparisonData.value = filteredWageList.map((ledger) => {
    const basic = basicList.value.find((b) => b.empNum === ledger.empNum);

    // 총급여 (개선전/개선후 동일하게 afterTotal 사용)
    const total = nc(ledger.total);

    // 개선후 (임금대장 데이터)
    const afterTaxable = nc(ledger.taxable);
    const afterNonTax = total - afterTaxable;
    const afterDeduction = nc(ledger.l_total);
    const afterNetPay = total - afterDeduction;
    const afterRetirement = nc(ledger.c_out);

    // 개선후 기관부담 4대보험
    const after_c_yeon = nc(ledger.c_yeon);
    const after_c_gun = nc(ledger.c_gun);
    const after_c_jang = nc(ledger.c_jang);
    const after_c_go = nc(ledger.c_go);
    const after_c_sil = nc(ledger.c_sil);
    const after_c_san = nc(ledger.c_san);
    const afterCompanyIns = after_c_yeon + after_c_gun + after_c_jang + after_c_go + after_c_sil + after_c_san;

    // 개선전 변수
    let beforeNonTax = 0;
    let beforeTaxable = 0;
    let beforeDeduction = 0;
    let beforeNetPay = 0;
    let beforeCompanyIns = 0;
    let beforeRetirement = 0;

    let before_c_yeon = 0, before_c_gun = 0, before_c_jang = 0, before_c_go = 0, before_c_sil = 0, before_c_san = 0;
    let beforeLunchPay = 0;

    // 개선후 비과세 항목 (임금대장)
    const afterDang = nc(ledger.dang);
    const afterLunch = nc(ledger.lunch);
    const afterCar = nc(ledger.car);
    const afterChildcare = nc(ledger.childcare);
    const afterProductionNonTax = afterNonTax - afterDang - afterLunch - afterCar - afterChildcare;

    // 개선전 비과세 항목
    let beforeDangPay = 0, beforeCarPay = 0, beforeChildcarePay = 0;

    if (basic) {
      beforeDangPay = nc(basic.eaDangPay);
      beforeLunchPay = nc(basic.eaLunchPay);
      beforeCarPay = nc(basic.eaCarPay);
      beforeChildcarePay = nc(basic.eaChildcarePay);

      // 개선전 비과세 (생산직비과세 미적용 - 당직+중식+차량+양육만)
      beforeNonTax = Math.floor((beforeLunchPay + beforeCarPay + beforeChildcarePay + beforeDangPay) / 10) * 10;
      beforeTaxable = Math.floor((total - beforeNonTax) / 10) * 10;

      // 개선전 근로자 공제 (개선후와 동일하게 사용)
      beforeDeduction = afterDeduction;
      beforeNetPay = total - beforeDeduction;

      // 개선전 기관부담 4대보험 계산
      before_c_yeon = after_c_yeon;
      before_c_gun = after_c_gun === 0 ? 0 : Math.max(after_c_gun, Math.floor((beforeTaxable * taxRate.tr_gun) / 10) * 10);
      before_c_jang = after_c_jang === 0 ? 0 : Math.max(after_c_jang, Math.floor((before_c_gun * taxRate.tr_jang) / 10) * 10);
      before_c_go = after_c_go === 0 ? 0 : Math.max(after_c_go, Math.floor((beforeTaxable * taxRate.tr_go) / 10) * 10);
      before_c_sil = after_c_sil === 0 ? 0 : Math.max(after_c_sil, Math.floor((beforeTaxable * taxRate.tr_sil) / 10) * 10);
      before_c_san = after_c_san;

      beforeCompanyIns = before_c_yeon + before_c_gun + before_c_jang + before_c_go + before_c_sil + before_c_san;
      beforeRetirement = afterRetirement === 0 ? 0 : Math.floor(((total - beforeLunchPay) / 12) / 10) * 10;
    }

    // 차액 계산
    const diffNonTax = afterNonTax - beforeNonTax;
    const diffCompanyIns = afterCompanyIns - beforeCompanyIns;
    const diffRetirement = afterRetirement - beforeRetirement;
    const diffNetPay = afterNetPay - beforeNetPay;
    const saving = diffCompanyIns + diffRetirement;

    // 툴팁 생성 (자세한 내역 포함)
    const tooltipTotal = `[총급여]\n\n◆ 임금대장 총급여\n= ${ac(total)}`;

    const tooltipBeforeNonTax = `[개선전 비과세]\n\n` +
      `※ 생산직비과세 미적용\n\n` +
      `◆ 비과세 항목\n` +
      `  당직수당: ${ac(beforeDangPay)}\n` +
      `  중식대: ${ac(beforeLunchPay)}\n` +
      `  차량비: ${ac(beforeCarPay)}\n` +
      `  양육비: ${ac(beforeChildcarePay)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 비과세 합계: ${ac(beforeNonTax)}`;

    const tooltipBeforeCompanyIns = `[개선전 기관부담 4대보험]\n\n` +
      `◆ 과세금액 ${ac(beforeTaxable)} 기준\n\n` +
      `  국민연금: ${ac(before_c_yeon)}${before_c_yeon === 0 ? ' (면제)' : ''}\n` +
      `  건강보험: ${ac(before_c_gun)}${before_c_gun === 0 ? ' (면제)' : ''}\n` +
      `  장기요양: ${ac(before_c_jang)}${before_c_jang === 0 ? ' (면제)' : ''}\n` +
      `  고용보험: ${ac(before_c_go)}${before_c_go === 0 ? ' (면제)' : ''}\n` +
      `  실업급여: ${ac(before_c_sil)}${before_c_sil === 0 ? ' (면제)' : ''}\n` +
      `  산재보험: ${ac(before_c_san)}${before_c_san === 0 ? ' (면제)' : ''}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 기관부담 합계: ${ac(beforeCompanyIns)}`;

    const tooltipBeforeRetirement = `[개선전 퇴직금]\n\n` +
      `◆ 계산식\n  (총급여 - 중식대) ÷ 12\n\n` +
      `◆ 산출내역\n` +
      `  총급여: ${ac(total)}\n` +
      `  (-) 중식대: ${ac(beforeLunchPay)}\n` +
      `  = ${ac(total - beforeLunchPay)} ÷ 12\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 퇴직금: ${ac(beforeRetirement)}${afterRetirement === 0 ? ' (면제)' : ''}`;

    const tooltipBeforeNetPay = `[개선전 지급급여]\n\n` +
      `◆ 계산식\n  총급여 - 근로자공제 = 지급급여\n\n` +
      `◆ 산출내역\n` +
      `  총급여: ${ac(total)}\n` +
      `  (-) 근로자공제: ${ac(beforeDeduction)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 지급급여: ${ac(beforeNetPay)}`;

    const tooltipAfterNonTax = `[개선후 비과세]\n\n` +
      `※ 생산직비과세 적용\n\n` +
      `◆ 비과세 항목\n` +
      `  생산직비과세: ${ac(afterProductionNonTax)}\n` +
      `  당직수당: ${ac(afterDang)}\n` +
      `  중식대: ${ac(afterLunch)}\n` +
      `  차량비: ${ac(afterCar)}\n` +
      `  양육비: ${ac(afterChildcare)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 비과세 합계: ${ac(afterNonTax)}`;

    const tooltipAfterCompanyIns = `[개선후 기관부담 4대보험]\n\n` +
      `◆ 과세금액 ${ac(afterTaxable)} 기준\n\n` +
      `  국민연금: ${ac(after_c_yeon)}${after_c_yeon === 0 ? ' (면제)' : ''}\n` +
      `  건강보험: ${ac(after_c_gun)}${after_c_gun === 0 ? ' (면제)' : ''}\n` +
      `  장기요양: ${ac(after_c_jang)}${after_c_jang === 0 ? ' (면제)' : ''}\n` +
      `  고용보험: ${ac(after_c_go)}${after_c_go === 0 ? ' (면제)' : ''}\n` +
      `  실업급여: ${ac(after_c_sil)}${after_c_sil === 0 ? ' (면제)' : ''}\n` +
      `  산재보험: ${ac(after_c_san)}${after_c_san === 0 ? ' (면제)' : ''}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 기관부담 합계: ${ac(afterCompanyIns)}`;

    const tooltipAfterRetirement = `[개선후 퇴직금]\n\n` +
      `◆ 임금대장 퇴직금\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 퇴직금: ${ac(afterRetirement)}${afterRetirement === 0 ? ' (면제)' : ''}`;

    const tooltipAfterNetPay = `[개선후 지급급여]\n\n` +
      `◆ 계산식\n  총급여 - 근로자공제 = 지급급여\n\n` +
      `◆ 산출내역\n` +
      `  총급여: ${ac(total)}\n` +
      `  (-) 근로자공제: ${ac(afterDeduction)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 지급급여: ${ac(afterNetPay)}`;

    const diffNonTaxStatus = diffNonTax > 0 ? '✓ 비과세 증가 (좋음)' : (diffNonTax < 0 ? '⚠ 비과세 감소' : '→ 변동 없음');
    const tooltipDiffNonTax = `[비과세 차액]\n\n` +
      `◆ 개선후 - 개선전 = 차액\n\n` +
      `  개선전: ${ac(beforeNonTax)}\n` +
      `  개선후: ${ac(afterNonTax)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 차액: ${ac(diffNonTax)}\n\n` +
      `${diffNonTaxStatus}`;

    const diffCompanyInsStatus = diffCompanyIns < 0 ? '✓ 기관부담 감소 (절감)' : (diffCompanyIns > 0 ? '⚠ 기관부담 증가' : '→ 변동 없음');
    const tooltipDiffCompanyIns = `[기관부담 차액]\n\n` +
      `◆ 개선후 - 개선전 = 차액\n\n` +
      `◆ 항목별 차액\n` +
      `  국민연금: ${ac(after_c_yeon - before_c_yeon)}\n` +
      `  건강보험: ${ac(after_c_gun - before_c_gun)}\n` +
      `  장기요양: ${ac(after_c_jang - before_c_jang)}\n` +
      `  고용보험: ${ac(after_c_go - before_c_go)}\n` +
      `  실업급여: ${ac(after_c_sil - before_c_sil)}\n` +
      `  산재보험: ${ac(after_c_san - before_c_san)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 차액: ${ac(diffCompanyIns)}\n\n` +
      `${diffCompanyInsStatus}`;

    const diffRetirementStatus = diffRetirement < 0 ? '✓ 퇴직금 감소 (절감)' : (diffRetirement > 0 ? '⚠ 퇴직금 증가' : '→ 변동 없음');
    const tooltipDiffRetirement = `[퇴직금 차액]\n\n` +
      `◆ 개선후 - 개선전 = 차액\n\n` +
      `  개선전: ${ac(beforeRetirement)}\n` +
      `  개선후: ${ac(afterRetirement)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 차액: ${ac(diffRetirement)}\n\n` +
      `${diffRetirementStatus}`;

    const diffNetPayStatus = diffNetPay > 0 ? '✓ 지급급여 증가 (좋음)' : (diffNetPay < 0 ? '⚠ 지급급여 감소' : '→ 변동 없음');
    const tooltipDiffNetPay = `[지급급여 차액]\n\n` +
      `◆ 개선후 - 개선전 = 차액\n\n` +
      `  개선전: ${ac(beforeNetPay)}\n` +
      `  개선후: ${ac(afterNetPay)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 차액: ${ac(diffNetPay)}\n\n` +
      `${diffNetPayStatus}`;

    const savingStatus = saving < 0 ? '✓ 기관비용 절감' : (saving > 0 ? '⚠ 기관비용 증가' : '→ 변동 없음');
    const tooltipSaving = `[기관 절감액]\n\n` +
      `◆ 기관부담 차액 + 퇴직금 차액\n\n` +
      `  기관부담 차액: ${ac(diffCompanyIns)}\n` +
      `  퇴직금 차액: ${ac(diffRetirement)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 절감액: ${ac(saving)}\n\n` +
      `${savingStatus}`;

    sum.total += total;
    sumBefore.nonTax += beforeNonTax;
    sumBefore.companyIns += beforeCompanyIns;
    sumBefore.retirement += beforeRetirement;
    sumBefore.netPay += beforeNetPay;
    sumAfter.nonTax += afterNonTax;
    sumAfter.companyIns += afterCompanyIns;
    sumAfter.retirement += afterRetirement;
    sumAfter.netPay += afterNetPay;

    return {
      empName: ledger.empName,
      empJob: ledger.empJob || "",
      total: ac(total),
      before: {
        nonTax: ac(beforeNonTax),
        companyIns: ac(beforeCompanyIns),
        retirement: ac(beforeRetirement),
        netPay: ac(beforeNetPay)
      },
      after: {
        nonTax: ac(afterNonTax),
        companyIns: ac(afterCompanyIns),
        retirement: ac(afterRetirement),
        netPay: ac(afterNetPay)
      },
      diff: {
        nonTax: ac(diffNonTax),
        companyIns: ac(diffCompanyIns),
        retirement: ac(diffRetirement),
        netPay: ac(diffNetPay)
      },
      saving: ac(saving),
      tooltip: {
        total: tooltipTotal,
        beforeNonTax: tooltipBeforeNonTax,
        beforeCompanyIns: tooltipBeforeCompanyIns,
        beforeRetirement: tooltipBeforeRetirement,
        beforeNetPay: tooltipBeforeNetPay,
        afterNonTax: tooltipAfterNonTax,
        afterCompanyIns: tooltipAfterCompanyIns,
        afterRetirement: tooltipAfterRetirement,
        afterNetPay: tooltipAfterNetPay,
        diffNonTax: tooltipDiffNonTax,
        diffCompanyIns: tooltipDiffCompanyIns,
        diffRetirement: tooltipDiffRetirement,
        diffNetPay: tooltipDiffNetPay,
        saving: tooltipSaving
      }
    };
  });

  const diffNonTax = sumAfter.nonTax - sumBefore.nonTax;
  const diffCompanyIns = sumAfter.companyIns - sumBefore.companyIns;
  const diffRetirement = sumAfter.retirement - sumBefore.retirement;
  const diffNetPay = sumAfter.netPay - sumBefore.netPay;
  const saving = diffCompanyIns + diffRetirement;

  totals.total = ac(sum.total);
  totals.before.nonTax = ac(sumBefore.nonTax);
  totals.before.companyIns = ac(sumBefore.companyIns);
  totals.before.retirement = ac(sumBefore.retirement);
  totals.before.netPay = ac(sumBefore.netPay);
  totals.after.nonTax = ac(sumAfter.nonTax);
  totals.after.companyIns = ac(sumAfter.companyIns);
  totals.after.retirement = ac(sumAfter.retirement);
  totals.after.netPay = ac(sumAfter.netPay);
  totals.diff.nonTax = ac(diffNonTax);
  totals.diff.companyIns = ac(diffCompanyIns);
  totals.diff.retirement = ac(diffRetirement);
  totals.diff.netPay = ac(diffNetPay);
  totals.saving = ac(saving);
  totals.yearSaving = ac(saving * 12);

  generateTotalsTooltip();
};

const generateTotalsTooltip = () => {
  const data = comparisonData.value;

  const nonTaxGood = data.filter(item => nc(item.diff.nonTax) > 0)
    .map(item => `${item.empName}(${item.empJob}): +${item.diff.nonTax}`);

  const companyInsGood = data.filter(item => nc(item.diff.companyIns) < 0)
    .map(item => `${item.empName}(${item.empJob}): ${item.diff.companyIns}`);

  const retirementGood = data.filter(item => nc(item.diff.retirement) < 0)
    .map(item => `${item.empName}(${item.empJob}): ${item.diff.retirement}`);

  const netPayGood = data.filter(item => nc(item.diff.netPay) > 0)
    .map(item => `${item.empName}(${item.empJob}): +${item.diff.netPay}`);

  const savingGood = data.filter(item => nc(item.saving) < 0)
    .map(item => `${item.empName}(${item.empJob}): ${item.saving}`);

  totalsTooltip.nonTax = `[비과세 차액] 합계: ${totals.diff.nonTax}\n` +
    `(+는 비과세 증가로 좋음)\n\n` +
    (nonTaxGood.length > 0 ? `✓ 비과세 증가 (${nonTaxGood.length}명):\n${nonTaxGood.slice(0, 5).join('\n')}${nonTaxGood.length > 5 ? '\n...' : ''}` : '');

  totalsTooltip.companyIns = `[기관부담 차액] 합계: ${totals.diff.companyIns}\n` +
    `(-는 기관부담 감소로 절감)\n\n` +
    (companyInsGood.length > 0 ? `✓ 기관부담 감소 (${companyInsGood.length}명):\n${companyInsGood.slice(0, 5).join('\n')}${companyInsGood.length > 5 ? '\n...' : ''}` : '');

  totalsTooltip.retirement = `[퇴직금 차액] 합계: ${totals.diff.retirement}\n` +
    `(-는 퇴직금 감소로 절감)\n\n` +
    (retirementGood.length > 0 ? `✓ 퇴직금 감소 (${retirementGood.length}명):\n${retirementGood.slice(0, 5).join('\n')}${retirementGood.length > 5 ? '\n...' : ''}` : '');

  totalsTooltip.netPay = `[지급급여 차액] 합계: ${totals.diff.netPay}\n` +
    `(+는 실수령액 증가로 좋음)\n\n` +
    (netPayGood.length > 0 ? `✓ 지급급여 증가 (${netPayGood.length}명):\n${netPayGood.slice(0, 5).join('\n')}${netPayGood.length > 5 ? '\n...' : ''}` : '');

  totalsTooltip.saving = `[기관 절감액] 합계: ${totals.saving}\n` +
    `(-는 비용 절감)\n\n◆ 연간 절감 예상: ${totals.yearSaving}\n\n` +
    (savingGood.length > 0 ? `✓ 비용 절감 (${savingGood.length}명):\n${savingGood.slice(0, 5).join('\n')}${savingGood.length > 5 ? '\n...' : ''}` : '');
};

const generatePdf = async () => {
  try {
    if (comparisonData.value.length === 0) {
      alert("비교 데이터가 없습니다.");
      return;
    }

    const sisulName = props.wageList[0]?.sisulName || "";
    const yearMonth = props.wageList[0]?.ym || "";

    const response = await axios.post("/api/getWageBeforeAfterPdf", {
      comparisonData: comparisonData.value,
      totals: { total: totals.total, before: totals.before, after: totals.after, diff: totals.diff, saving: totals.saving, yearSaving: totals.yearSaving },
      sisulName,
      yearMonth
    });

    const pdfBase64 = response.data.pdf;
    const binaryString = window.atob(pdfBase64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const newWindow = window.open(url, "_blank");
    if (newWindow) {
      newWindow.focus();
    } else {
      alert("팝업 차단 설정을 확인해주세요.");
    }
  } catch (error) {
    console.error("PDF 생성 에러:", error);
    alert("PDF 생성 중 오류가 발생했습니다.");
  }
};
</script>

<style scoped>
.wageBeforeAfterModalCard {
  background-color: #fff;
  padding: 24px;
  border-radius: 10px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.modal-header h2 {
  margin: 0;
  font-weight: 600;
}

.close-btn {
  width: 35px;
  height: 35px;
  cursor: pointer;
}

.info-section {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-weight: 500;
  color: #666;
  font-size: 14px;
}

.info-value {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.info-value.highlight {
  color: #1976d2;
  font-weight: 700;
}

.edit-btn {
  min-width: auto !important;
  padding: 0 4px !important;
}

.date-input {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  font-family: inherit;
}

.action-section {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.modal-content {
  min-height: 200px;
}

.unit-label {
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  padding-right: 10px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.comparison-table-wrapper {
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
  margin-top: 16px;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.comparison-table th,
.comparison-table td {
  border: 1px solid #ddd;
  padding: 6px 10px;
  white-space: nowrap;
}

.comparison-table thead th {
  background-color: #f5f5f5;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
}

.comparison-table thead tr:first-child th {
  top: 0;
}

.comparison-table thead tr:nth-child(2) th {
  top: 32px;
}

.sticky-col {
  position: sticky;
  left: 0;
  background-color: #fff;
  z-index: 5;
  font-weight: 500;
}

.col-no {
  width: 35px;
  min-width: 35px;
}

.sticky-col-name {
  position: sticky;
  left: 35px;
  background-color: #fff;
  z-index: 5;
  font-weight: 500;
}

.comparison-table thead .sticky-col,
.comparison-table thead .sticky-col-name {
  z-index: 15;
  background-color: #f5f5f5;
}

.total-row .sticky-col,
.total-row .sticky-col-name {
  background-color: #f0f0f0 !important;
}

.before-header {
  background-color: #fff3e0 !important;
  color: #e65100;
}

.after-header {
  background-color: #e8f5e9 !important;
  color: #2e7d32;
}

.diff-header {
  background-color: #e3f2fd !important;
  color: #1565c0;
}

.diff-sub-header {
  background-color: #e3f2fd !important;
  color: #1565c0;
  font-size: 11px;
}

.before-cell {
  background-color: #fffaf5;
}

.after-cell {
  background-color: #f5fff5;
}

.diff-cell {
  background-color: #f5f9ff;
}

.saving-header {
  background-color: #fce4ec !important;
  color: #c2185b;
}

.saving-cell {
  background-color: #fff5f7;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.font-bold {
  font-weight: 700;
}

.total-row td {
  background-color: #f0f0f0 !important;
  font-weight: 600;
}

.tooltip-cell {
  cursor: help;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
}

.highlight-green {
  color: #2e7d32;
}

.highlight-red {
  color: #d32f2f;
}

.highlight-blue {
  color: #1565c0;
}

.summary-section {
  padding: 20px 0;
}

.summary-cards {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.summary-card {
  flex: 1;
  min-width: 300px;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #ddd;
}

.summary-card h3 {
  margin: 0 0 16px 0;
  font-size: 15px;
  font-weight: 600;
}

.before-card {
  background-color: #fff8f0;
  border-color: #ffcc80;
}

.before-card h3 {
  color: #e65100;
}

.after-card {
  background-color: #f0fff0;
  border-color: #a5d6a7;
}

.after-card h3 {
  color: #2e7d32;
}

.diff-card {
  background-color: #f0f7ff;
  border-color: #90caf9;
}

.diff-card h3 {
  color: #1565c0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
}

.summary-row .label {
  color: #666;
}

.summary-row .value {
  font-weight: 500;
}

.summary-row.total {
  border-top: 1px solid #ddd;
  margin-top: 8px;
  padding-top: 12px;
  font-weight: 600;
}

.summary-row.total .label,
.summary-row.total .value {
  font-weight: 600;
}

.summary-row.highlight-row {
  background-color: #e3f2fd;
  margin: -8px -14px 0 -14px;
  padding: 10px 14px;
  border-radius: 4px;
}

.summary-row.highlight-row .value {
  font-weight: 600;
  color: #1565c0;
}

.divider {
  height: 1px;
  background-color: #ddd;
  margin: 12px 0;
}

.large {
  font-size: 18px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  margin-top: 20px;
}
</style>
